import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './ChapterDetails.css';

const ChapterDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id, chapterId } = useParams();
  const [currentVideo, setCurrentVideo] = useState(0);

  // Mock data
  const chapter = {
    id: 1,
    title: 'Introduction to Calculus',
    description: 'This chapter introduces the fundamental concepts of calculus including limits, derivatives, and integrals.',
    videos: [
      { id: 1, title: 'What is Calculus?', duration: '10:30', isWatched: true },
      { id: 2, title: 'Understanding Limits', duration: '15:45', isWatched: true },
      { id: 3, title: 'Introduction to Derivatives', duration: '12:20', isWatched: false },
      { id: 4, title: 'Basic Derivative Rules', duration: '18:10', isWatched: false }
    ],
    materials: [
      { id: 1, type: 'PDF', title: 'Chapter Notes', size: '2.5 MB', icon: '📄' },
      { id: 2, type: 'Link', title: 'Additional Resources', icon: '🔗' },
      { id: 3, type: 'Video', title: 'Extra Examples', icon: '🎥' }
    ],
    quiz: {
      id: 1,
      title: 'Chapter Quiz',
      questionsCount: 10,
      duration: '15 min',
      isCompleted: false
    }
  };

  const handleVideoClick = (videoId: number) => {
    navigate(`/course/${id}/chapter/${chapterId}/video/${videoId}`);
  };

  const handleQuizClick = () => {
    navigate(`/course/${id}/chapter/${chapterId}/quiz/${chapter.quiz.id}`);
  };

  return (
    <div className="chapter-details-page">
      <header className="chapter-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>{chapter.title}</h1>
        <div className="header-spacer"></div>
      </header>

      <div className="chapter-details-content">
        {/* Chapter Description */}
        <div className="chapter-description-card">
          <p>{chapter.description}</p>
        </div>

        {/* Videos Section */}
        <div className="videos-section">
          <h2 className="section-title">
            Videos ({chapter.videos.length})
          </h2>
          {chapter.videos.map((video, index) => (
            <div
              key={video.id}
              className={`video-item ${video.isWatched ? 'watched' : ''} ${index === currentVideo ? 'active' : ''}`}
              onClick={() => handleVideoClick(video.id)}
            >
              <div className="video-number">{index + 1}</div>
              <div className="video-info">
                <h3 className="video-title">{video.title}</h3>
                <span className="video-duration">⏱️ {video.duration}</span>
              </div>
              <div className="video-status">
                {video.isWatched ? (
                  <span className="watched-icon">✓</span>
                ) : (
                  <button className="play-button-small">▶</button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Materials Section */}
        {chapter.materials.length > 0 && (
          <div className="materials-section">
            <h2 className="section-title">Materials</h2>
            {chapter.materials.map((material) => (
              <div key={material.id} className="material-item">
                <div className="material-icon">{material.icon}</div>
                <div className="material-info">
                  <h3 className="material-title">{material.title}</h3>
                  {material.size && (
                    <span className="material-size">{material.size}</span>
                  )}
                </div>
                <button className="download-button">⬇</button>
              </div>
            ))}
          </div>
        )}

        {/* Quiz Section */}
        {chapter.quiz && (
          <div className="quiz-section">
            <div className="quiz-card">
              <div className="quiz-icon-large">📝</div>
              <div className="quiz-info-large">
                <h3>{chapter.quiz.title}</h3>
                <div className="quiz-meta">
                  <span>{chapter.quiz.questionsCount} questions</span>
                  <span>⏱️ {chapter.quiz.duration}</span>
                </div>
              </div>
              <button 
                className={`quiz-button ${chapter.quiz.isCompleted ? 'completed' : ''}`}
                onClick={handleQuizClick}
              >
                {chapter.quiz.isCompleted ? 'Review' : 'Start Quiz'}
              </button>
            </div>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ChapterDetails;
